import { html, cx, useState, useEffect, useRef, useMemo, useCallback } from '../h.js'
import { Icon } from '../icons.js'
import { t, plural } from '../i18n.js'
import {
  clockTime, duration, fileSize, renderText, systemText, blobUrl, copyText
} from '../format.js'
import { Avatar, useToast, QUICK_REACTIONS } from '../components.js'
import { useApp } from '../store.js'
import { KIND, ROOM_TYPE, parseCollectLink, normalizeButtons, pressExtra } from '@pine/core'
import { invitePreview } from './dialogs.js'

/* ------------------------------------------------------------------ *
 * Attachment loading
 * ------------------------------------------------------------------ */

/*
 * One object URL per attachment, shared by everything that shows it.
 *
 * The rule that matters here is that nothing revokes a URL somebody might
 * still be rendering. Closing the lightbox used to revoke the one it was
 * handed — which was this shared one — so every thumbnail behind it was left
 * pointing at a dead blob. Nothing showed it until the next re-mount, and then
 * the <img> failed and the browser drew its alt text: leaving a chat and
 * coming back turned a screen of photos into a list of file names.
 *
 * So entries are dropped only when the cache is over budget and nothing is
 * holding them, and a URL that turns out to be dead anyway repairs itself
 * through the reload below rather than becoming a caption.
 */
const media = new Map() // blobId -> { url, refs }
const MEDIA_LIMIT = 64

function remember (id, url) {
  const existing = media.get(id)
  if (existing) {
    // Two components asked at once. Keep the first and drop the duplicate,
    // rather than leaking it for the life of the tab.
    URL.revokeObjectURL(url)
    return existing
  }
  const entry = { url, refs: 0 }
  media.set(id, entry)

  // Map iterates in insertion order, so this drops the least recently added
  // that nobody is looking at. Anything still on screen is skipped.
  for (const [key, held] of media) {
    if (media.size <= MEDIA_LIMIT) break
    if (held.refs > 0) continue
    media.delete(key)
    URL.revokeObjectURL(held.url)
  }
  return entry
}

function release (id) {
  const entry = media.get(id)
  if (entry && entry.refs > 0) entry.refs--
}

function forget (id) {
  const entry = media.get(id)
  if (!entry) return
  media.delete(id)
  URL.revokeObjectURL(entry.url)
}

/**
 * Past this a photo or video waits for a tap rather than loading itself: the
 * whole file is decrypted into memory to be shown, and a phone that did that
 * for every long video scrolled past was a phone that reloaded the tab.
 */
export const AUTO_LOAD_MAX = 40 * 1024 * 1024

/**
 * Blobs are pulled from peers on demand and decrypted locally, so an
 * attachment can be "in the message" long before its bytes exist on this
 * device. This hook keeps that state visible instead of showing a broken
 * image, and shares one object URL across every component that wants it.
 *
 * A download that stalls is not a dead end. The blocks stay asked for, and
 * arrive whenever the sender is back — so an attachment in that state keeps
 * looking for them, and shows itself the moment they are here, instead of
 * sitting on an error until somebody leaves the chat and comes back.
 */
export function useAttachment (room, attachment, { auto = true } = {}) {
  const id = attachment ? attachment.blobId : null
  const [attempt, setAttempt] = useState(0)
  const [wanted, setWanted] = useState(auto)
  const reloaded = useRef(false)
  const [state, setState] = useState(() => {
    const cached = id && media.get(id)
    return { url: cached ? cached.url : null, loading: false, error: null, progress: 0 }
  })

  useEffect(() => { if (auto) setWanted(true) }, [auto])

  useEffect(() => {
    if (!room || !id || !wanted) return

    let alive = true
    let held = false
    const hold = (entry) => {
      entry.refs++
      held = true
      return entry
    }

    const cached = media.get(id)
    if (cached) {
      hold(cached)
      setState({ url: cached.url, loading: false, error: null, progress: 1 })
    } else {
      setState((s) => ({ url: null, loading: true, error: null, progress: s.progress || 0 }))
      room.downloadAttachment(attachment, {
        onProgress: (progress) => {
          if (alive) setState((s) => (s.loading ? { ...s, progress } : s))
        }
      })
        .then((bytes) => {
          if (!bytes) throw new Error(t('conv.attachmentGone'))
          // Cached even if this component has gone: the bytes were fetched
          // over the network and scrolling back should not do it twice.
          const entry = remember(id, blobUrl(bytes, attachment.mime))
          if (!alive) return
          hold(entry)
          setState({ url: entry.url, loading: false, error: null, progress: 1 })
        })
        .catch((error) => { if (alive) setState({ url: null, loading: false, error, progress: 0 }) })
    }

    return () => {
      alive = false
      if (held) release(id)
    }
  }, [room, id, wanted, attempt])

  useEffect(() => {
    if (!state.error || !room || !room.blobs || !id) return
    let alive = true
    const look = setInterval(() => {
      room.blobs.has(attachment)
        .then((here) => { if (alive && here) setAttempt((n) => n + 1) })
        .catch(() => {})
    }, 4000)
    return () => {
      alive = false
      clearInterval(look)
    }
  }, [state.error, room, id])

  /** Load it now: a tap on a large file, or on one that stalled. */
  const retry = useCallback(() => {
    setWanted(true)
    setAttempt((n) => n + 1)
  }, [])

  /**
   * The URL did not load. Throw it away and fetch again — once. A blob URL can
   * die under a page that was restored from the back/forward cache, and the
   * repair costs a decrypt from disk. Retrying forever would not.
   */
  const reload = useCallback(() => {
    if (!id || reloaded.current) return
    reloaded.current = true
    forget(id)
    setAttempt((n) => n + 1)
  }, [id])

  return { ...state, wanted, retry, reload }
}

/** What to say about a download that is not coming right now. */
export function attachmentErrorText (error) {
  if (error && error.code === 'ATTACHMENT_UNAVAILABLE') return t('media.waiting')
  return (error && error.message) || t('err.generic')
}

/**
 * How big a photo or video is drawn before it has loaded.
 *
 * It has to be a real size. The bubble is as wide as what is in it, and a
 * placeholder with no width of its own made the bubble — and the placeholder
 * with it — as narrow as the timestamp: a photo still on its way was a small
 * blank bubble nobody could tell was a photo at all. Sized from the picture's
 * own proportions, it takes the place the picture will fill.
 */
/**
 * The space a photo or video takes in the chat: its own shape, within
 * reason.
 *
 * The shape used to be held between 0.45 and 2.2 and the picture cut to
 * fill it, so a panorama lost its sides and a long screenshot its top and
 * bottom — and a video whose size the sender's device never learnt showed
 * as 4:3, cropped. Now the box follows the picture between a third and
 * three; past that the box stops and the picture is shown whole inside it,
 * with a margin, never cut. `natural` is the size the loaded file turned out
 * to have, which wins over what the message says.
 */
export const MEDIA_RATIO_MIN = 1 / 3
export const MEDIA_RATIO_MAX = 3

export function mediaBox (attachment, natural = null) {
  const w = (natural && natural.width) || (attachment && attachment.width) || 0
  const h = (natural && natural.height) || (attachment && attachment.height) || 0
  const real = w && h ? w / h : 4 / 3
  const ratio = Math.min(MEDIA_RATIO_MAX, Math.max(MEDIA_RATIO_MIN, real))
  const width = ratio >= 1 ? 320 : Math.round(Math.max(150, 360 * ratio))
  return { width, ratio, whole: ratio !== real }
}

/** A small preview carried inside the message itself, shown until the real one is here. */
export function thumbUrl (attachment) {
  return attachment && attachment.thumb ? 'data:image/jpeg;base64,' + attachment.thumb : null
}

/* ------------------------------------------------------------------ *
 * Message row
 * ------------------------------------------------------------------ */

/**
 * A message on its way out: shown the moment it is sent, and replaced by the
 * real one as soon as that is in the timeline.
 */
export function PendingRow ({ item }) {
  return html`
    <div class="msg out single tail pending" aria-busy="true">
      <div class="bubble">
        ${item.files.map((name) => html`
          <div class="text dim small" key=${name}>${Icon.attach(13)} ${name}</div>`)}
        ${item.text && html`<div class="text">${item.text}</div>`}
        <span class="meta">
          <span>${clockTime(item.at)}</span>
          <span class="ticks" title=${t('conv.sending')}>${Icon.clock(13)}</span>
        </span>
      </div>
    </div>`
}

export function MessageRow ({
  message, room, prev, next, isMine, authorName, showAuthor,
  reactions, readByAll, onAction, menu
}) {
  const store = useApp()

  if (message.kind === KIND.SYSTEM) {
    return html`<div class="sys-msg"><span>${systemText(message, undefined, store.client.identity && store.client.identity.id)}</span></div>`
  }

  const grouped = (a, b) => a && b &&
    a.author === b.author &&
    b.kind !== KIND.SYSTEM &&
    Math.abs(b.timestamp - a.timestamp) < 5 * 60 * 1000

  const withPrev = grouped(prev, message)
  const withNext = grouped(message, next)
  const position = !withPrev && !withNext ? 'single'
    : !withPrev ? 'head'
      : withNext ? 'mid' : 'foot'

  const items = messageMenu(message, { isMine, room, store, onAction })

  return html`
    <div class=${cx('msg', isMine ? 'out' : 'in', position, !withNext && 'tail')}
         data-id=${message.id}
         onContextMenu=${(e) => menu.open(e, items)}>
      <div class="bubble">
        ${showAuthor && !isMine && !withPrev && html`
          <div class="author" style="color:var(--accent)">${authorName}</div>`}

        ${message.forwardFrom && html`
          <div class="forward-note">${Icon.forwardMsg(13)} ${message.forwardName || t('conv.forward')}</div>`}

        ${message.replyTo && html`
          <${ReplyQuote} room=${room} id=${message.replyTo} />`}

        ${message.extra && message.extra.story && html`
          <div class="story-note">
            ${Icon.image(13)} ${message.extra.story.reaction ? t('stories.reactedHeader') : t('stories.replyHeader')}
          </div>`}

        ${message.deleted
          ? html`<div class="text dim" style="font-style:italic">${t('conv.deleted')}</div>`
          : html`<${MessageBody} message=${message} room=${room} />`}

        <span class="meta">
          ${message.editedAt > 0 && html`<span>${t('conv.edited')}</span>`}
          <span>${clockTime(message.timestamp)}</span>
          ${isMine && html`
            <span class="ticks">${readByAll ? Icon.checkDouble(14) : Icon.check(14)}</span>`}
        </span>

        ${!isMine && !message.deleted && html`<${BotButtons} message=${message} room=${room} />`}

        ${reactions && reactions.length > 0 && html`
          <div class="reactions">
            ${reactions.map((r) => html`
              <button key=${r.emoji}
                      class=${cx('reaction', r.accounts.includes(room.me) && 'mine')}
                      onClick=${() => room.toggleReaction(message.id, r.emoji)}>
                <span>${r.emoji}</span><span>${r.count}</span>
              </button>`)}
          </div>`}
      </div>

      <div class="msg-actions">
        <button class="icon-btn" title=${t('conv.reply')}
                onClick=${() => onAction('reply', message)}>${Icon.reply(16)}</button>
        <button class="icon-btn" title="React"
                onClick=${(e) => menu.open(e, reactionMenu(room, message), 'end')}>${Icon.smile(16)}</button>
        <button class="icon-btn" onClick=${(e) => menu.open(e, items, 'end')}>${Icon.more(16)}</button>
      </div>
    </div>`
}

/**
 * The buttons a bot put under its message.
 *
 * A button with data posts its own label as a message, carrying the data —
 * the bot reads it like anything else typed into the chat, so a press is
 * delivered whenever the two devices meet, not only if both happen to be
 * online in the same second. A button with a link opens it.
 */
function BotButtons ({ message, room }) {
  const store = useApp()
  const toast = useToast()
  const rows = normalizeButtons(message.extra && message.extra.buttons)
  if (!rows.length) return null

  async function press (button) {
    if (button.url) {
      if (store.platform && store.platform.openExternal) store.platform.openExternal(button.url)
      else window.open(button.url, '_blank', 'noopener,noreferrer')
      return
    }
    try {
      await room.send({ text: button.text, extra: pressExtra(message.id, button.data) })
    } catch (err) {
      toast(err.message || t('err.generic'), { error: true })
    }
  }

  return html`
    <div class="bot-buttons">
      ${rows.map((row, i) => html`
        <div class="bot-row" key=${i}>
          ${row.map((button, j) => html`
            <button key=${j} class="bot-btn" disabled=${!room.writable}
                    onClick=${() => press(button)}>
              <span class="truncate">${button.text}</span>
              ${button.url && html`<span class="bot-btn-link">↗</span>`}
            </button>`)}
        </div>`)}
    </div>`
}

function MessageBody ({ message, room }) {
  const store = useApp()
  const attachment = message.attachments && message.attachments[0]

  switch (message.kind) {
    case KIND.IMAGE:
    case KIND.GIF:
    case KIND.VIDEO:
      return html`
        <${MediaAttachment} room=${room} attachment=${attachment} kind=${message.kind}
          onOpen=${(url) => store.openLightbox({ src: url, mime: attachment.mime, name: attachment.name })} />
        ${message.text && html`<div class="text">${renderText(message.text, message.entities)}</div>`}`

    case KIND.AUDIO:
      return html`<${VoiceNote} room=${room} attachment=${attachment} />`

    case KIND.FILE:
      return html`
        <${FileAttachment} room=${room} attachment=${attachment} />
        ${message.text && html`<div class="text">${renderText(message.text, message.entities)}</div>`}`

    case KIND.POLL:
      return html`<${Poll} room=${room} message=${message} />`

    case KIND.LOCATION: {
      const extra = message.extra || {}
      return html`
        <a class="att-file" target="_blank" rel="noopener noreferrer"
           href=${'https://www.openstreetmap.org/?mlat=' + extra.lat + '&mlon=' + extra.lon + '#map=16/' + extra.lat + '/' + extra.lon}>
          <span class="glyph">${Icon.location(20)}</span>
          <span class="grow"><span class="name">${extra.label || 'Location'}</span>
          <span class="size">${Number(extra.lat).toFixed(4)}, ${Number(extra.lon).toFixed(4)}</span></span>
        </a>`
    }

    case KIND.STICKER:
      return html`<div style="font-size:56px;line-height:1.1">${message.text}</div>`

    default: {
      const invite = findInvite(message.text)
      const preview = !invite && message.extra && message.extra.preview
      return html`
        <div class="text">${renderText(message.text, message.entities, { onLink: linkHandler(store) })}</div>
        ${invite && html`<${InviteCard} code=${invite} store=${store} />`}
        ${preview && html`<${LinkPreview} preview=${preview} store=${store} />`}`
    }
  }
}

const INVITE_RE = /(?:https?:\/\/[^\s/]+\/i\/|pine:\/\/join\/)([a-z0-9]{20,})/i

/** The first Pine invite in a message, as its code. */
function findInvite (text) {
  const match = String(text || '').match(INVITE_RE)
  return match ? match[1] : null
}

/**
 * Where a tapped link goes.
 *
 * A Pine invite or a collectible-name link is handled here, in the app that
 * is already open. Left to the browser, it went to the web version — a second
 * Pine, with a fresh empty account, in a tab — which is exactly what somebody
 * with the app installed did not want. Everything else leaves the app.
 */
function linkHandler (store) {
  return (event, href) => {
    const token = parseCollectLink(href)
    if (token) {
      event.preventDefault()
      store.showDialog({ type: 'collect', token })
      return
    }
    const code = findInvite(href)
    if (code) {
      event.preventDefault()
      store.showDialog({ type: 'join', code })
      return
    }
    if (store.platform && store.platform.openExternal) {
      event.preventDefault()
      store.platform.openExternal(href)
    }
  }
}

function InviteCard ({ code, store }) {
  const preview = invitePreview(code)
  if (!preview) return null
  return html`
    <div class="invite-card in-message">
      <${Avatar} name=${preview.name} id=${code} size="sm" square=${preview.channel} />
      <div class="grow" style="min-width:0">
        <div class="strong truncate">${preview.name || t('invite.unnamed')}</div>
        <div class="xs dim">${preview.channel ? t('invite.channel') : t('invite.group')}</div>
      </div>
      <button class="btn btn-sm btn-primary" onClick=${() => store.showDialog({ type: 'join', code })}>
        ${preview.channel ? t('invite.subscribe') : t('invite.join')}
      </button>
    </div>`
}

/**
 * A link's card: the page's name, a line about it and its picture.
 *
 * Made on the sender's device when the message was written and carried inside
 * it, so opening the chat fetches nothing from anywhere — see
 * packages/pine-ui/lib/link-preview.js.
 */
function LinkPreview ({ preview, store }) {
  if (!preview || !preview.url || !(preview.title || preview.description)) return null
  const open = (event) => linkHandler(store)(event, preview.url)
  return html`
    <a class="link-preview" href=${preview.url} target="_blank" rel="noopener noreferrer" onClick=${open}>
      ${preview.image && html`<img src=${'data:image/jpeg;base64,' + preview.image} alt="" loading="lazy" />`}
      <span class="lp-body">
        ${preview.site && html`<span class="lp-site">${preview.site}</span>`}
        ${preview.title && html`<span class="lp-title">${preview.title}</span>`}
        ${preview.description && html`<span class="lp-text">${preview.description}</span>`}
      </span>
    </a>`
}

function MediaAttachment ({ room, attachment, kind, onOpen }) {
  const large = (attachment.size || 0) > AUTO_LOAD_MAX
  const { url, loading, error, progress, wanted, retry, reload } =
    useAttachment(room, attachment, { auto: !large })
  const [natural, setNatural] = useState(null)
  const box = mediaBox(attachment, natural)

  // The file's own size, once it is here, when the message said otherwise
  // or nothing at all.
  const measured = (width, height) => {
    if (!width || !height) return
    const said = attachment.width && attachment.height ? attachment.width / attachment.height : 0
    if (said && Math.abs(said - width / height) / said < 0.03) return
    setNatural({ width, height })
  }
  const thumb = thumbUrl(attachment)
  const video = kind === KIND.VIDEO

  // A video plays where it is, with its own controls; only a photo opens full
  // screen. Before either has loaded, a tap is a request to load it.
  const onClick = () => {
    if (url) {
      if (!video) onOpen(url)
    } else if (!loading) {
      retry()
    }
  }

  let note = null
  if (!url) {
    if (loading) {
      note = progress > 0 && progress < 1
        ? Math.round(progress * 100) + '% · ' + fileSize(attachment.size)
        : t('media.loading')
    } else if (error) {
      note = attachmentErrorText(error)
    } else if (!wanted) {
      note = t('media.tapToLoad') + ' · ' + fileSize(attachment.size)
    }
  }

  return html`
    <div class=${cx('att-media', video && 'video', url && 'ready', box.whole && 'whole')}
         style=${{ width: box.width + 'px', aspectRatio: String(box.ratio) }}
         onClick=${onClick}>
      ${thumb && !url && html`<img class="att-thumb" src=${thumb} alt="" aria-hidden="true" />`}
      ${url
        ? (video
            ? html`<video src=${url} poster=${thumb || undefined} controls playsinline
                          preload="metadata" onError=${reload}
                          onLoadedMetadata=${(e) => measured(e.currentTarget.videoWidth, e.currentTarget.videoHeight)}></video>`
            : html`<img src=${url} alt=${attachment.name} loading="lazy" onError=${reload}
                        onLoad=${(e) => measured(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)} />`)
        : html`
          <div class="att-overlay">
            ${loading
              ? html`<${ProgressRing} value=${progress} />`
              : html`<span class="att-action">
                  ${error ? Icon.refresh(20) : (video ? Icon.play(22) : Icon.download(20))}
                </span>`}
            ${note && html`<span class="att-note">${note}</span>`}
          </div>`}
      ${video && !url && attachment.duration > 0 && html`
        <span class="att-duration">${duration(attachment.duration)}</span>`}
    </div>`
}

/** A download as a ring filling up, with a spinner while nothing is known yet. */
function ProgressRing ({ value = 0 }) {
  if (!(value > 0)) return html`<div class="spinner lg"></div>`
  const r = 16
  const length = 2 * Math.PI * r
  return html`
    <svg class="att-ring" width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
      <circle cx="20" cy="20" r=${r} fill="none" stroke="currentColor" stroke-opacity=".3" stroke-width="3" />
      <circle cx="20" cy="20" r=${r} fill="none" stroke="currentColor" stroke-width="3"
              stroke-linecap="round" transform="rotate(-90 20 20)"
              stroke-dasharray=${length} stroke-dashoffset=${length * (1 - Math.min(1, value))} />
    </svg>`
}

function FileAttachment ({ room, attachment }) {
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState(0)
  const toast = useToast()

  async function download () {
    if (busy) return
    setBusy(true)
    setProgress(0)
    try {
      const bytes = await room.downloadAttachment(attachment, { onProgress: setProgress })
      if (!bytes) throw new Error(t('conv.attachmentGone'))
      const url = blobUrl(bytes, attachment.mime)
      const a = document.createElement('a')
      a.href = url
      a.download = attachment.name || 'file'
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 20000)
    } catch (err) {
      toast(attachmentErrorText(err), { error: true })
    } finally {
      setBusy(false)
    }
  }

  return html`
    <button class="att-file" onClick=${download}>
      <span class="glyph">${busy ? html`<div class="spinner"></div>` : Icon.file(20)}</span>
      <span class="grow" style="text-align:left">
        <span class="name truncate">${attachment.name}</span>
        <span class="size">
          ${busy && progress > 0 && progress < 1 ? Math.round(progress * 100) + '% · ' : ''}${fileSize(attachment.size)}
        </span>
      </span>
      ${Icon.download(17)}
    </button>`
}

function VoiceNote ({ room, attachment }) {
  const { url, loading } = useAttachment(room, attachment)
  const audioRef = useRef(null)
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)

  const bars = useMemo(() => {
    const wave = attachment.waveform
    if (wave && wave.length) return Array.from(wave)
    // Stable pseudo-waveform so a note without stored peaks still looks right.
    return Array.from({ length: 34 }, (_, i) => 60 + ((i * 53) % 140))
  }, [attachment.waveform])

  function toggle () {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) audio.play()
    else audio.pause()
  }

  return html`
    <div class="att-voice">
      <button class="icon-btn" onClick=${toggle} disabled=${!url}>
        ${loading ? html`<div class="spinner"></div>` : (playing ? Icon.pause(18) : Icon.play(18))}
      </button>
      <div class="waveform" onClick=${(e) => {
        const audio = audioRef.current
        if (!audio || !audio.duration) return
        const rect = e.currentTarget.getBoundingClientRect()
        audio.currentTime = ((e.clientX - rect.left) / rect.width) * audio.duration
      }}>
        ${bars.map((value, i) => html`
          <i key=${i} class=${cx(i / bars.length <= progress && 'played')}
             style=${{ height: Math.max(3, (value / 255) * 26) + 'px' }}></i>`)}
      </div>
      <span class="xs" style="opacity:.75;font-variant-numeric:tabular-nums">
        ${duration(attachment.duration || 0)}
      </span>
      ${url && html`
        <audio ref=${audioRef} src=${url} preload="metadata"
               onPlay=${() => setPlaying(true)}
               onPause=${() => setPlaying(false)}
               onEnded=${() => { setPlaying(false); setProgress(0) }}
               onTimeUpdate=${(e) => setProgress(e.target.currentTime / (e.target.duration || 1))}></audio>`}
    </div>`
}

function Poll ({ room, message }) {
  const extra = message.extra || {}
  const [votes, setVotes] = useState({ tally: {}, voters: {}, total: 0 })
  const [people, setPeople] = useState({})
  const multiple = !!(extra.flags & 1)
  const anonymous = !!(extra.flags & 2)

  useEffect(() => {
    let alive = true
    const load = () => room.view.votes(message.id).then((v) => { if (alive) setVotes(v) }).catch(() => {})
    load()
    const off = room.on('update', load)
    return () => { alive = false; off() }
  }, [room, message.id])

  // Who is who, but only for a poll that shows it. An anonymous poll must not
  // even look up the names: the whole promise of the switch is that this side
  // of the app has no interest in them.
  useEffect(() => {
    if (anonymous) return
    let alive = true
    room.view.members().then((list) => {
      if (!alive) return
      setPeople(Object.fromEntries(list.map((m) => [m.account, m])))
    }).catch(() => {})
    return () => { alive = false }
  }, [room, anonymous, votes.total])

  const mine = votes.voters[room.me] || []
  const votersFor = (index) => anonymous
    ? []
    : Object.keys(votes.voters).filter((account) => (votes.voters[account] || []).includes(index))

  return html`
    <div class="poll">
      <div class="q">${extra.question || message.text}</div>
      ${(extra.options || []).map((option, i) => {
        const count = votes.tally[i] || 0
        const share = votes.total ? count / votes.total : 0
        const chosen = mine.includes(i)
        const voters = votersFor(i)
        return html`
          <div key=${i}>
            <button class=${cx('poll-option', chosen && 'chosen')}
                    onClick=${() => room.vote(message.id, multiple ? toggleIn(mine, i) : [i])}>
              <span class="fill" style=${{ width: (share * 100).toFixed(1) + '%' }}></span>
              <span class="label">
                <span>${chosen ? '● ' : ''}${option}</span>
                <span>${Math.round(share * 100)}%</span>
              </span>
            </button>
            ${voters.length > 0 && html`
              <div class="poll-voters">
                ${voters.slice(0, 3).map((account) => html`
                  <${Avatar} key=${account} size="xs" id=${account}
                    name=${(people[account] && people[account].name) || ''}
                    src=${people[account] && people[account].avatar} />`)}
                ${voters.length > 3 && html`<span class="xs dim">+${voters.length - 3}</span>`}
              </div>`}
          </div>`
      })}
      <div class="poll-foot xs">
        ${anonymous
          ? t('poll.anonymousNote')
          : (votes.total === 0 ? t('poll.noVotes') : plural('voted', votes.total))}
        ${multiple && html`<span> · ${t('poll.multiple')}</span>`}
      </div>
    </div>`
}

function toggleIn (list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
}

function ReplyQuote ({ room, id }) {
  const [target, setTarget] = useState(null)
  const [author, setAuthor] = useState('')

  useEffect(() => {
    let alive = true
    room.view.message(id).then(async (m) => {
      if (!alive || !m) return
      setTarget(m)
      const member = await room.view.member(m.author)
      if (alive) setAuthor(member ? (member.name || '') : '')
    }).catch(() => {})
    return () => { alive = false }
  }, [room, id])

  if (!target) return null

  return html`
    <button class="reply-quote" onClick=${() => {
      const node = document.querySelector('[data-id="' + id + '"]')
      if (node) {
        node.scrollIntoView({ block: 'center', behavior: 'smooth' })
        node.animate([{ background: 'var(--accent-soft)' }, { background: 'transparent' }],
          { duration: 1200, easing: 'ease-out' })
      }
    }}>
      <span class="who">${author || t('chats.you')}</span>
      <span class="snippet">${previewOfTarget(target)}</span>
    </button>`
}

function previewOfTarget (message) {
  if (message.deleted) return t('conv.deleted')
  if (message.text) return message.text
  const attachment = message.attachments && message.attachments[0]
  return attachment ? attachment.name : ''
}

/* ------------------------------------------------------------------ *
 * Menus
 * ------------------------------------------------------------------ */

function messageMenu (message, { isMine, room, store, onAction }) {
  const isChannel = room.meta && room.meta.type === ROOM_TYPE.CHANNEL

  return [
    { label: t('conv.reply'), icon: Icon.reply(17), onSelect: () => onAction('reply', message) },
    message.text && {
      label: t('conv.copy'),
      icon: Icon.copy(17),
      onSelect: () => copyText(message.text)
    },
    { label: t('conv.forward'), icon: Icon.forwardMsg(17), onSelect: () => onAction('forward', message) },
    { label: t('conv.pin'), icon: Icon.pin(17), onSelect: () => onAction('pin', message) },
    isMine && !message.deleted && {
      label: t('conv.edit'),
      icon: Icon.edit(17),
      onSelect: () => onAction('edit', message)
    },
    { separator: true },
    /*
     * Reporting, in channels only.
     *
     * A channel post is already public to everyone in the channel, so handing
     * a copy to the operator gives away nothing its audience does not have.
     * The same is not true of a group or a one-to-one chat, and a per-message
     * report there would quietly turn a private conversation into an upload —
     * so reporting a person, with the conversation attached, is a separate and
     * much louder thing (see the chat info panel).
     */
    isChannel && !isMine && !message.deleted && {
      label: t('report.action'),
      icon: Icon.flag(17),
      onSelect: () => store.showDialog({
        type: 'report', kind: 'message', roomId: room.id, messageId: message.id
      })
    },
    !message.deleted && {
      label: t('conv.delete'),
      icon: Icon.trash(17),
      danger: true,
      onSelect: () => store.showDialog({ type: 'delete-message', roomId: room.id, messageId: message.id })
    }
  ].filter(Boolean)
}

function reactionMenu (room, message) {
  return QUICK_REACTIONS.map((emoji) => ({
    label: emoji + '  ',
    onSelect: () => room.toggleReaction(message.id, emoji)
  }))
}
